/**
 * Safe API fetch utility that prevents "body stream already read" errors
 */

/**
 * Only redirect to trusted domains (Stripe checkout or same origin).
 * Prevents open-redirect attacks where a malicious server could return
 * an arbitrary URL and the frontend blindly follows it.
 */
export function safeRedirect(url) {
  if (!url || typeof url !== "string") return;
  try {
    const parsed = new URL(url);
    const trusted = [
      "checkout.stripe.com",
      window.location.hostname,
    ];
    if (trusted.some(h => parsed.hostname === h || parsed.hostname.endsWith(`.${h}`))) {
      window.location.href = url;
    } else {
      console.error("Blocked redirect to untrusted URL:", url);
    }
  } catch {
    console.error("Blocked redirect to invalid URL:", url);
  }
}

export async function safeFetch(url, options = {}) {
  const response = await fetch(url, {
    credentials: "include",
    ...options,
  });
  
  // Read response as text first to prevent double-read issues
  const text = await response.text();
  
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      // If not JSON, return text as-is
      data = { message: text };
    }
  }
  
  return {
    ok: response.ok,
    status: response.status,
    data,
    error: !response.ok ? (data?.detail || data?.message || `Error ${response.status}`) : null
  };
}

export async function apiGet(endpoint) {
  return safeFetch(endpoint);
}

export async function apiPost(endpoint, body) {
  return safeFetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function apiPut(endpoint, body) {
  return safeFetch(endpoint, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function apiDelete(endpoint) {
  return safeFetch(endpoint, {
    method: "DELETE",
  });
}
